import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import RollsBrewSplash from "./pwa/RollsBrewSplash";
import { orderStartupState } from "../lib/orderStartup";

const StartupContext = createContext(null);
export const useOrderStartup = () => useContext(StartupContext);

// The sole /order splash owner. Brand verification still controls mounting
// the app; route loading and the real public-menu request run behind this art.
export default function OrderStartupBoundary({ children, prepareRoute }) {
  const { pathname } = useLocation();
  const active = /^\/order\/?$/.test(pathname);
  const [session, setSession] = useState({ active, id: 0, finished: false, parts: {} });
  if (session.active !== active) {
    setSession({ active, id: session.id + 1, finished: false, parts: {} });
  }
  const id = session.id;
  const report = useCallback((part, status) => {
    if (!active) return;
    setSession((current) => current.id !== id || current.parts[part] === status ? current
      : { ...current, parts: { ...current.parts, [part]: status } });
  }, [active, id]);
  const finish = useCallback(() => {
    setSession((current) => current.id !== id ? current : { ...current, finished: true });
  }, [id]);
  const visible = active && !session.finished;
  const state = orderStartupState(session.parts);
  const context = useMemo(() => ({ active, visible, report }), [active, visible, report]);
  useEffect(() => {
    // Download code in parallel with verification, but never mount the page
    // until the existing brand gates permit it. RouteLoadBoundary owns errors.
    if (active) prepareRoute?.().catch(() => {});
  }, [active, prepareRoute]);
  return (
    <StartupContext.Provider value={context}>
      <div aria-hidden={visible ? true : undefined} inert={visible ? true : undefined}>
        {children}
      </div>
      {visible && <RollsBrewSplash ready={state.ready || state.blocked} status={state.status} onDone={finish} />}
    </StartupContext.Provider>
  );
}
